import { useCallback } from 'react';

import { PopupEventHandlers, useBrowserPopup } from '@/lib/browser-popup/useBrowserPopup';

interface PopupResponse {
  code?: string;
  state?: string;
  error?: string;
  error_description?: string;
}

export const useAuthPopup = () => {
  const { open, close } = useBrowserPopup();

  const openAuthPopup = useCallback(
    async (url: string): Promise<PopupResponse> => {
      return new Promise<PopupResponse>((resolve, reject) => {
        let messageHandler: ((event: MessageEvent) => void) | null = null;
        let popup: Window | null = null;

        // Set up message handler before opening popup
        messageHandler = (event: MessageEvent) => {
          // Verify origin matches
          if (event.origin !== window.location.origin) {
            return;
          }

          // Check if it's our auth callback message
          if (event.data?.type === 'auth-callback') {
            const response: PopupResponse = {
              code: event.data.code || undefined,
              state: event.data.state || undefined,
              error: event.data.error || undefined,
              error_description: event.data.error_description || undefined,
            };

            // Clean up
            if (messageHandler) {
              window.removeEventListener('message', messageHandler);
            }

            if (response.error) {
              reject(new Error(response.error_description || 'Authentication failed'));
            } else {
              resolve(response);
            }

            // Close popup
            if (popup && !popup.closed) {
              close(popup);
            }
          }
        };

        // Add message listener
        window.addEventListener('message', messageHandler);

        const handlers: PopupEventHandlers = {
          onBlocked: () => {
            if (messageHandler) {
              window.removeEventListener('message', messageHandler);
            }
            reject(new Error('Popup was blocked by browser. Please enable popups and try again.'));
          },
          onClosed: () => {
            // If popup was closed without completing auth
            if (messageHandler) {
              window.removeEventListener('message', messageHandler);
              reject(new Error('Authentication cancelled'));
            }
          },
        };

        popup = open(url, { name: 'Authorize Token' }, handlers);

        if (!popup) {
          if (messageHandler) {
            window.removeEventListener('message', messageHandler);
          }
          reject(new Error('Failed to open popup'));
          return;
        }
      });
    },
    [open, close]
  );

  return {
    openAuthPopup,
  };
};
